import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  FormErrorMessage,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Text,
  useToast
} from '@chakra-ui/react';
import { submitFeedback, submitGuestFeedback } from '../../Redux/Ticket/userTicketActions';

const FeedbackModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const signedIn = useSelector((state) => Boolean(state.auth?.token));
  
  // Get submission state from Redux
  const { loading, error, fieldErrors } = useSelector(state => state.ticket?.submission || {});
  
  // Form state
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    email: ''
  });
  
  // Listen for report message events
  useEffect(() => {
    const handleReportEvent = (event) => {
      console.log('openReportModal event received in FeedbackModal with detail:', event.detail);
      const { subject, message } = event.detail;
      
      console.log('Setting form data in FeedbackModal:', { subject, message });
      // Pre-fill the form data (keep any email the guest already typed)
      setFormData((prev) => ({
        subject: subject || '',
        message: message || '',
        email: (prev && prev.email) || ''
      }));
      
      // Open the modal if it's not already open
      console.log('FeedbackModal isOpen state:', isOpen);
      if (!isOpen) {
        console.log('Trying to prevent modal from closing');
        onClose(false); // Prevent modal from closing
      }
    };
    
    // Add event listener
    console.log('Adding openReportModal event listener in FeedbackModal');
    document.addEventListener('openReportModal', handleReportEvent);
    
    // Clean up
    return () => {
      document.removeEventListener('openReportModal', handleReportEvent);
    };
  }, [isOpen, onClose]);
  
  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  // Handle form submission — members submit as themselves (account ticket);
  // guests send anonymously via the public endpoint (email optional, by design).
  const handleSubmit = async (e) => {
    e.preventDefault();

    const result = signedIn
      ? await dispatch(submitFeedback({ subject: formData.subject, message: formData.message }))
      : await dispatch(submitGuestFeedback({
          subject: formData.subject,
          message: formData.message,
          email: (formData.email || '').trim() || undefined
        }));

    if (result.success) {
      toast({
        title: 'Thanks for letting us know',
        description: result.message || 'Your feedback was sent. We appreciate your input.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      onClose();
      // Reset form
      setFormData({
        subject: '',
        message: '',
        email: ''
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent bg="gray.800" color="white">
        <ModalHeader>Submit Feedback</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {error && (
            <Alert status="error" mb={4}>
              <AlertIcon />
              <AlertTitle>Error!</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <form onSubmit={handleSubmit}>
            <VStack spacing={4}>
              {!signedIn && (
                <Text fontSize="sm" color="whiteAlpha.700" mb={1}>
                  No account needed. Your message reaches us as a note — add your email only
                  if you&rsquo;d like us to reply to it.
                </Text>
              )}
              <FormControl isRequired={signedIn} isInvalid={fieldErrors?.subject}>
                <FormLabel>Subject</FormLabel>
                <Input 
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="Briefly describe your feedback"
                />
                {fieldErrors?.subject && (
                  <FormErrorMessage>{fieldErrors.subject}</FormErrorMessage>
                )}
              </FormControl>
              
              <FormControl isRequired isInvalid={fieldErrors?.message}>
                <FormLabel>Your Feedback</FormLabel>
                <Textarea 
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="We'd love to hear your thoughts, suggestions, or concerns..."
                  rows={6}
                />
                {fieldErrors?.message && (
                  <FormErrorMessage>{fieldErrors.message}</FormErrorMessage>
                )}
              </FormControl>
              
              {!signedIn && (
                <FormControl isInvalid={fieldErrors?.email}>
                  <FormLabel>Email (optional)</FormLabel>
                  <Input 
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@example.com — only if you'd like a reply"
                    autoComplete="email"
                  />
                  {fieldErrors?.email && (
                    <FormErrorMessage>{fieldErrors.email}</FormErrorMessage>
                  )}
                </FormControl>
              )}
            </VStack>
          </form>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button 
            colorScheme="blue" 
            onClick={handleSubmit}
            isLoading={loading}
            loadingText="Submitting"
          >
            Submit Feedback
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default FeedbackModal;
